import { ContextDocumentContent } from '@flowx/designer/modules/ai/agents/documents/api/documents.api'
import { ExecutionPlanAction } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import type {
  AIContentType,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { Slice } from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { MediaLibraryResource } from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import {
  CodeEditorSelection,
  ModelMarkerWithId,
} from '@flowx/designer/modules/monaco-code-editor/types'

export type ChatMessage = {
  prompt: string
  type: MessageType
}

export type ExecutionPlanActionBase = Pick<
  ExecutionPlanAction,
  'localId' | 'agent' | 'name' | 'description' | 'target' | 'state' | 'context'
>

export type AiDeveloperContext = {
  code?: string
  language: string
  datasource: DataSourceType[]
  aiContentType?: AIContentType
}

export type GenerateProcessContext = {
  target: ProcessTarget
}

export type GenerateBusinessRuleContext = AiDeveloperContext

export type ExplainBusinessRuleContext = GenerateBusinessRuleContext

export type AddThreadMessageContext = {
  prompt: string
  conversationId: string
  threadId: string
}

export type BusinessRuleTarget = {
  nodeId: number
  actionId: number
  index: number
}

export type ExpressionTarget = {
  nodeId: number
  type: ExpressionTargetType
  index?: number
  propName?: string
  templateId?: number
}

export type UiTarget = {
  nodeId: number
  navigationAreaId: string
}

export enum ExpressionTargetType {
  Hide = 'hide',
  Disabled = 'disabled',
  Override = 'override',
  ComputedValue = 'computed_value',
  ConditionalStyling = 'conditional_styling',
  Unknown = 'unknown',
}

export type ProcessTarget = {
  processId: number
}

export type DataSourceType = {
  name: string
  description: string
  type: DataModelAttributeType
  properties: {
    isArray: boolean
    reference?: string
    exampleValue?: string
    name: string
    type: DataModelAttributeType
    description: string
  }[]
}

export enum FixCodeType {
  Error = 'error',
  Warning = 'warning',
}

export type FixCodeWarningsContext = AiDeveloperContext & {
  warnings: ModelMarkerWithId[]
  fixCodeType: FixCodeType.Error | FixCodeType.Warning
}

export type CodeSelectionContext = AiDeveloperContext & {
  selection: CodeEditorSelection
}

export type ReusableFunctionContext = {
  code: string
  language: string
  functionDataModel: DataSourceType[]
  projectDataModel: DataSourceType[]
  functionMetadata: {
    name: string
    description: string
    inputParamsType: string
    outputParamsType: string
    input_slices: Slice[]
    output_slices: Slice[]
  }
}

export type ScriptNodeContext = {
  nodeId: string
  // Current visible editor contents, including unsaved edits.
  code: string
  language: string
  workflowDataModel: DataSourceType[]
}

export type ProcessMessageContext = {
  conversationId: string
  threadId: string
  prompt: string
  target: ProcessTarget
}

export type ConversationDocument = {
  id: string
  diDocumentId: string
  cmsDocument: MediaLibraryResource
  filename: string
  content: ContextDocumentContent[]
  status: string
  tags: string[]
  isReady: boolean
  modifiedDate: string
}

export type CustomComponentAiContext = {
  sourceCode: string | null
  styles: string | null
  packageJson: string | null
  componentName: string
  uniqueIdentifier: string
  availableLibraries: string
  jsxFilename: string
  cssFilename: string
  datasource: DataSourceType[]
}

export {
  Action,
  ActionCategory,
  ActionsDiff,
  AttachedFile,
  AttachedFileType,
  SelectedActions,
  SerializedSelectedActions,
} from './ai-conversation.commons'
